/**
 * dp-staffel-rechner.js — Preisrechner „Wie viele Kalender?“ (<dp-staffel-rechner>).
 *
 * Staffel-USP (Inhaber 2026-09-18: „das wird unser USP“): ein Schieberegler über die echten
 * Staffelstufen (1/2/3/5/10 Stück – nur belegte Stufen sind Rasterpunkte, der Regler läuft
 * über den Index der Stufe), dazu Gesamtpreis, „je Stück“, Ersparnis in Prozent, ein Balken
 * je Stück, der mit der Menge schrumpft, und eine Anlass-Beispielzeile („Einer für euch,
 * einer für Oma – zusammen 20,56 €“).
 *
 * Daten: snippets/dp-staffel-rechner.liquid rendert das Grundgerüst serverseitig (Stand für
 * 2 Stück, keine Zahl ohne Datenquelle) und legt die Staffeln der wählbaren Formate als JSON
 * in <script type="application/json" data-dp-rechner-data> ab – dieselben Zeichenketten wie
 * part 'tiers' in snippets/dp-format-defaults.liquid ("Menge:Gesamt:jeStück:Prozent|…").
 * Auf der Produktseite (data-follow-picker) folgt der Rechner der gewählten Formatkachel
 * über das Event `dp:format-change` (assets/dp-format-picker.js liefert detail.tiers).
 *
 * Die Rechenlogik (parseTiersString, priceFor, snapQuantity, ariaValueText, exampleLine,
 * formelText) ist DOM-frei exportiert und in tests/designer/dp-staffel-rechner.test.mjs geprüft.
 */

export const FORMAT_CHANGE_EVENT = 'dp:format-change';
export const CHANGE_EVENT = 'dp:staffel-change';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/** @typedef {{ quantity: number, price: number }} Tier */
/** @typedef {{ quantity: number, total: number, each: number, base: number, pct: number, extra: number | null }} PriceRow */

/** @param {number} value */
export const roundCents = (value) => Math.round(value * 100) / 100;

/** "20,56 €" (geschütztes Leerzeichen wie Intl) – dieselbe Schreibweise wie die Kaufspalte. */
export function formatEuro(value) {
  return euro.format(value);
}

/** Deutsche Preisschreibweise "20,56" (Liquid-Snippets) → 20.56; ungültig → null. */
function parseDe(text) {
  const n = Number.parseFloat(String(text ?? '').trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * Sortierte, bereinigte Staffel: nur ganze Mengen ≥ 1 mit Preis > 0, je Menge ein Eintrag.
 * @param {unknown} list
 * @returns {Tier[]}
 */
export function normalizeTiers(list) {
  if (!Array.isArray(list)) return [];
  const byQuantity = new Map();
  for (const item of list) {
    const quantity = Number(item?.quantity);
    const price = Number(item?.price);
    if (!Number.isInteger(quantity) || quantity < 1 || !Number.isFinite(price) || price <= 0) continue;
    if (!byQuantity.has(quantity)) byQuantity.set(quantity, { quantity, price: roundCents(price) });
  }
  return [...byQuantity.values()].sort((a, b) => a.quantity - b.quantity);
}

/**
 * Staffel-Zeichenkette aus snippets/dp-format-defaults.liquid (part 'tiers'):
 * "1:18,44:18,44:0|2:20,56:10,28:44|…" → [{ quantity: 1, price: 18.44 }, …].
 * @param {unknown} raw
 * @returns {Tier[]}
 */
export function parseTiersString(raw) {
  if (typeof raw !== 'string') return [];
  return normalizeTiers(
    raw
      .split('|')
      .map((row) => row.split(':'))
      .map((cells) => ({ quantity: Number.parseInt(cells[0] ?? '', 10), price: parseDe(cells[1]) }))
  );
}

/**
 * Nächste belegte Stufe zu einer gewünschten Menge (Zwischenwerte runden, bei gleichem Abstand
 * die kleinere Stufe – nichts wird nach oben „geschönt“). Leere Staffel → null.
 * @param {Tier[]} tiers
 * @param {number} wanted
 */
export function snapQuantity(tiers, wanted) {
  if (!tiers.length) return null;
  const target = Number.isFinite(Number(wanted)) ? Number(wanted) : 1;
  let best = tiers[0].quantity;
  let bestDistance = Math.abs(best - target);
  for (const { quantity } of tiers) {
    const distance = Math.abs(quantity - target);
    if (distance < bestDistance) {
      best = quantity;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Preis für eine Menge: Gesamt, je Stück (auf Cent gerundet), Ersparnis je Stück gegenüber dem
 * Einzelpreis in ganzen Prozent (nie negativ), Aufpreis gegenüber der Stufe darunter.
 * Nur echte Staffelzeilen – Zwischenmengen werden auf die nächste belegte Stufe gerundet.
 * @param {Tier[]} tiers
 * @param {number} wanted
 * @returns {PriceRow | null}
 */
export function priceFor(tiers, wanted) {
  const quantity = snapQuantity(tiers, wanted);
  if (quantity == null) return null;
  const index = tiers.findIndex((t) => t.quantity === quantity);
  const tier = tiers[index];
  const base = tiers.find((t) => t.quantity === 1)?.price ?? tiers[0].price / tiers[0].quantity;
  const each = roundCents(tier.price / tier.quantity);
  const pct = quantity > 1 && base > 0 ? Math.max(0, Math.round((1 - each / base) * 100)) : 0;
  const previous = index > 0 ? tiers[index - 1] : null;
  const extra = previous ? roundCents(tier.price - previous.price) : null;
  return { quantity, total: tier.price, each, base: roundCents(base), pct, extra };
}

/**
 * Die eine Formel: „2 Kalender für 20,56 €“ + „je Stück 10,28 € statt 18,44 €“ –
 * identisch mit part 'formel' in snippets/dp-format-defaults.liquid. Ohne 2er-Stufe null.
 * @param {Tier[]} tiers
 */
export function formelText(tiers) {
  const two = tiers.find((t) => t.quantity === 2);
  const one = tiers.find((t) => t.quantity === 1);
  if (!two || !one) return null;
  const each = roundCents(two.price / 2);
  return {
    line: `2 Kalender für ${formatEuro(two.price)}`,
    sub: `je Stück ${formatEuro(each)} statt ${formatEuro(one.price)}`,
  };
}

/** aria-valuetext des Reglers: „2 Kalender, 20,56 €“ („Kalender“ ist Singular wie Plural). */
export function ariaValueText(row) {
  if (!row) return '';
  return `${row.quantity} Kalender, ${formatEuro(row.total)}`;
}

/** Balkenlänge „je Stück“ in Prozent des Einzelpreises (mindestens 6 %, damit er sichtbar bleibt). */
export function barPercent(row) {
  if (!row || !(row.base > 0)) return 100;
  return Math.max(6, Math.min(100, Math.round((row.each / row.base) * 100)));
}

/** „−44 % je Stück“; leer ohne Ersparnis. */
export function savingLabel(row) {
  return row && row.pct > 0 ? `−${row.pct} % je Stück` : '';
}

const NUMBER_WORDS = { 2: 'zwei', 3: 'drei', 4: 'vier', 5: 'fünf', 6: 'sechs', 7: 'sieben', 8: 'acht', 9: 'neun', 10: 'zehn' };

/**
 * Anlass-Beispielzeile je Menge (Inhaber: „Einer für euch, einer für Oma – zusammen 20,56 €“).
 * @param {PriceRow | null} row
 */
export function exampleLine(row) {
  if (!row) return '';
  const total = formatEuro(row.total);
  switch (row.quantity) {
    case 1:
      return `Einer für euch – ${total}`;
    case 2:
      return `Einer für euch, einer für Oma – zusammen ${total}`;
    case 3:
      return `Einer für euch, einer für Oma und einer für die Patentante – zusammen ${total}`;
    case 5:
      return `Für die ganze Familie – fünf Kalender zusammen ${total}`;
    case 10:
      return `Fürs Team – zehn Kalender zusammen ${total}`;
    default:
      return `${NUMBER_WORDS[row.quantity] ?? row.quantity} Kalender mit demselben Motiv – zusammen ${total}`;
  }
}

/**
 * Wählbare Formate aus dem JSON des Snippets: nur Einträge mit gültiger Staffel.
 * @param {unknown} raw
 * @returns {{ id: string, name: string, format: string, fmt: string, tiers: Tier[] }[]}
 */
export function normalizeProducts(raw) {
  const list = Array.isArray(raw?.products) ? raw.products : Array.isArray(raw) ? raw : [];
  const out = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const id = String(item.id ?? '').trim();
    const tiers = Array.isArray(item.tiers) ? normalizeTiers(item.tiers) : parseTiersString(item.tiers);
    if (!/^\d{1,6}$/.test(id) || tiers.length < 2) continue;
    const fmt = String(item.fmt ?? '').trim();
    out.push({
      id,
      name: String(item.name ?? '').trim(),
      format: String(item.format ?? '').trim(),
      fmt: /^[a-z0-9_]{1,48}$/.test(fmt) ? fmt : '',
      tiers,
    });
  }
  return out;
}

/** Designer-Link zur gewählten Kombination (Vertrag: assets/dp-designer-link.js, `fmt` statt `format`). */
export function designerHref(product, base = '/pages/designer') {
  const params = new URLSearchParams();
  params.set('product', product.id);
  // ?pick=1: der Kunde hat hier Produkt und Format selbst gewaehlt (Rechner-Chip) – das Quiz fragt nicht noch einmal.
  if (product.fmt) { params.set('fmt', product.fmt); params.set('pick', '1'); }
  return `${base}?${params.toString()}`;
}

const BaseElement = typeof HTMLElement === 'function' ? HTMLElement : class {};

class DpStaffelRechner extends BaseElement {
  /** @type {ReturnType<typeof normalizeProducts>} */
  #products = [];
  /** @type {Tier[]} */
  #tiers = [];
  #quantity = 2;
  #context = '';
  #href = '';
  #started = false;
  #onFormatChange = (event) => this.#applyDetail(event.detail);

  connectedCallback() {
    if (this.#started) return;
    this.#started = true;

    try {
      const json = this.querySelector('script[type="application/json"][data-dp-rechner-data]');
      this.#products = normalizeProducts(json ? JSON.parse(json.textContent || 'null') : null);
    } catch (error) {
      console.warn('[dp-staffel-rechner] Staffeldaten unlesbar', error);
      this.#products = [];
    }

    const wanted = Number.parseInt(this.dataset.quantity ?? '2', 10);
    this.#quantity = Number.isInteger(wanted) && wanted > 0 ? wanted : 2;

    const first = this.#products[0];
    if (first) this.#useProduct(first, false);

    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-dp-product]');
      if (chip && this.contains(chip)) {
        const product = this.#products.find((p) => p.id === chip.dataset.dpProduct);
        if (product) this.#useProduct(product, true);
        return;
      }
      const step = event.target.closest('[data-dp-step]');
      if (step && this.contains(step)) {
        const quantity = Number.parseInt(step.dataset.dpStep ?? '', 10);
        if (Number.isInteger(quantity)) this.#setQuantity(quantity);
      }
    });

    const range = this.#range();
    if (range) {
      range.addEventListener('input', () => {
        const tier = this.#tiers[Number.parseInt(range.value, 10)];
        if (tier) this.#setQuantity(tier.quantity);
      });
    }

    if (this.dataset.followPicker === 'true') {
      document.addEventListener(FORMAT_CHANGE_EVENT, this.#onFormatChange);
      const picker = document.querySelector('dp-format-picker');
      if (picker && picker.selection) this.#applyDetail(picker.selection);
    }

    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    document.removeEventListener(FORMAT_CHANGE_EVENT, this.#onFormatChange);
  }

  /** Aktuelle Stufe (für Tests und andere Module). */
  get row() {
    return priceFor(this.#tiers, this.#quantity);
  }

  #range() {
    const el = this.querySelector('input[type="range"][data-dp-range]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /** Formatkachel der Produktseite (detail.tiers in Euro-Zahlen, detail.name = „DIN A4 hoch“). */
  #applyDetail(detail) {
    if (!detail || !Array.isArray(detail.tiers)) return;
    const tiers = normalizeTiers(detail.tiers);
    const product = this.#products[0];
    this.#context = [product?.name, detail.name].filter(Boolean).join(', ');
    this.#href = '';
    this.#setTiers(tiers);
  }

  /** @param {ReturnType<typeof normalizeProducts>[number]} product */
  #useProduct(product, focusChip) {
    for (const chip of this.querySelectorAll('[data-dp-product]')) {
      const active = chip.dataset.dpProduct === product.id;
      chip.setAttribute('aria-pressed', String(active));
      if (active && focusChip) chip.focus({ preventScroll: true });
    }
    this.#context = [product.name, product.format].filter(Boolean).join(', ');
    this.#href = designerHref(product, this.dataset.designerBase || '/pages/designer');
    this.#setTiers(product.tiers);
  }

  /** @param {Tier[]} tiers */
  #setTiers(tiers) {
    this.#tiers = tiers;
    this.hidden = tiers.length < 2;
    if (this.hidden) return;
    this.#renderScale();
    this.#setQuantity(snapQuantity(tiers, this.#quantity) ?? tiers[0].quantity);
  }

  #renderScale() {
    const scale = this.querySelector('[data-dp-scale]');
    if (!scale) return;
    scale.replaceChildren(
      ...this.#tiers.map((tier) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dp-rechner__step';
        button.dataset.dpStep = String(tier.quantity);
        button.textContent = String(tier.quantity);
        button.setAttribute('aria-label', `${tier.quantity} Kalender, ${formatEuro(tier.price)}`);
        return button;
      })
    );
    const range = this.#range();
    if (range) range.max = String(Math.max(0, this.#tiers.length - 1));
  }

  #setQuantity(quantity) {
    const row = priceFor(this.#tiers, quantity);
    if (!row) return;
    this.#quantity = row.quantity;
    const index = this.#tiers.findIndex((t) => t.quantity === row.quantity);

    const range = this.#range();
    if (range) {
      if (range.value !== String(index)) range.value = String(index);
      range.setAttribute('aria-valuetext', ariaValueText(row));
      range.style.setProperty('--dp-range-fill', `${this.#tiers.length > 1 ? (index / (this.#tiers.length - 1)) * 100 : 0}%`);
    }
    for (const step of this.querySelectorAll('[data-dp-step]')) {
      step.setAttribute('aria-current', step.dataset.dpStep === String(row.quantity) ? 'true' : 'false');
    }

    const set = (selector, text) => {
      for (const el of this.querySelectorAll(selector)) el.textContent = text;
    };
    set('[data-dp-qty]', String(row.quantity));
    set('[data-dp-total]', formatEuro(row.total));
    set('[data-dp-each]', formatEuro(row.each));
    set('[data-dp-base]', formatEuro(row.base));
    set('[data-dp-example]', exampleLine(row));
    set('[data-dp-context]', this.#context);
    const saving = savingLabel(row);
    for (const el of this.querySelectorAll('[data-dp-saving]')) {
      el.textContent = saving;
      el.hidden = !saving;
    }
    for (const el of this.querySelectorAll('[data-dp-bar]')) el.style.setProperty('--dp-bar', `${barPercent(row)}%`);
    for (const el of this.querySelectorAll('[data-dp-bar-base]')) el.hidden = row.quantity === 1;
    for (const link of this.querySelectorAll('a[data-dp-cta]')) {
      if (this.#href) link.href = this.#href;
    }

    this.dispatchEvent(new CustomEvent(CHANGE_EVENT, { bubbles: true, detail: { ...row, context: this.#context } }));
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('dp-staffel-rechner')) {
  customElements.define('dp-staffel-rechner', DpStaffelRechner);
}
